/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  Card,
  Icon,
  ToggleButtonGroup,
  Typography,
  UtilityButton,
  useTheme,
} from '@quickbit/qb-design-system';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAsyncFn, useMount } from 'react-use';
import { QBLoader, Transactions } from 'components';
import { useSelected, useSetSelected } from 'tabs/ProcessingUsers/context';
import {
  exportTransactions,
  searchProcessingUsers,
  useProcessingUserAlgorandTransactions,
} from '../../api';
import { AlgorandProcessingTransactions } from './AlgorandProcessingTransactions';
import { RecursiveDisabledInput } from './RecursiveDisabledInput';

type Tab = 'details' | 'transactions' | 'algorandProcessingTransactions';

export const ProcessingUserDetails = () => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  const { userId } = useParams();
  const setSelected = useSetSelected();
  const user = useSelected();
  const [tab, setTab] = useState<Tab>('details');
  const processingUserId = user?.userId;

  const [{ loading: searchLoading }, search] = useAsyncFn(async () => {
    if (userId) {
      const { processingUsers } = await searchProcessingUsers({
        filters: { userId },
        pagination: { page: 1, size: 1 },
      });
      if (processingUsers.length > 0) {
        setSelected(processingUsers[0]);
      }
      return processingUsers;
    }
  }, [userId, setSelected]);

  const [{ loading: exportLoading }, exportTransactionsFn] = useAsyncFn(
    async () => {
      if (user) {
        const blob = await exportTransactions(user.userId);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${user.userId}-transactions.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      }
    }
  );

  const {
    data: algorandTransactionsData,
    isLoading: algorandTransactionsLoading,
  } = useProcessingUserAlgorandTransactions(
    processingUserId,
    tab === 'algorandProcessingTransactions'
  );

  useEffect(() => {
    if (!user) {
      void search();
    }
  }, [user, search]);

  useMount(() => {
    if (user && user.userId !== userId) {
      void search();
    }
  });

  const openAlgorandWalletInExplorer = () => {
    if (!algorandTransactionsData?.walletAddress) {
      return;
    }
    const explorerUrl =
      process.env.REACT_APP_ENVIRONMENT === 'production'
        ? 'https://explorer.perawallet.app/address/'
        : 'https://testnet.explorer.perawallet.app/address/';
    window.open(
      `${explorerUrl}${algorandTransactionsData.walletAddress}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  if (searchLoading) return <QBLoader />;

  return (
    <>
      <Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            gap: spacing.xxs,
            alignItems: 'center',
          }}
        >
          <div
            css={{ display: 'flex', gap: spacing.xxxs, alignItems: 'center' }}
          >
            <div
              css={{
                cursor: 'pointer',
                ':hover': {
                  textDecoration: 'underline',
                  textDecorationColor: palette.grey.x300,
                },
              }}
              onClick={() => navigate('/users/processing')}
            >
              <Typography variant="subtitle" color={palette.grey.x300}>
                Processing
              </Typography>
            </div>
            <div
              css={{
                transform: 'rotate(180deg)',
                svg: {
                  fill: 'none',
                  stroke: palette.grey.x300,
                },
              }}
            >
              <Icon name="chevron" />
            </div>
            <Typography variant="subtitle">{user && user.userId}</Typography>
          </div>
        </div>
      </Header>
      <Card>
        <div css={{ marginBottom: spacing.xxs }}>
          <ToggleButtonGroup
            onChange={(option) => setTab(option as Tab)}
            selected={tab}
            toggleOptions={[
              { label: 'Details', value: 'details' },
              { label: 'Transactions', value: 'transactions' },
              {
                label: 'Algorand Processing Transactions',
                value: 'algorandProcessingTransactions',
              },
            ]}
          />
        </div>
        {tab === 'details' && user && (
          <div css={{ display: 'grid', gap: spacing.xxs }}>
            <RecursiveDisabledInput item={user} />
          </div>
        )}
        {tab === 'transactions' && (
          <>
            <div css={{ marginBottom: spacing.xxs }}>
              <Typography variant="subtitle">Transactions</Typography>
              <UtilityButton
                icon="ALGO"
                css={{ maxWidth: 500 }}
                text="Export Transactions"
                loading={exportLoading}
                disabled={exportLoading}
                onClick={exportTransactionsFn}
              />
            </div>
            {user && <Transactions key={user.userId} userId={user.userId} />}
          </>
        )}
        {tab === 'algorandProcessingTransactions' && (
          <>
            <div
              css={{
                marginBottom: spacing.xxs,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: spacing.xxxs,
              }}
            >
              <Typography variant="subtitle">
                Algorand Processing Transactions
              </Typography>
              <UtilityButton
                text="Open Wallet In Blockchain Explorer"
                variant="secondary"
                onClick={openAlgorandWalletInExplorer}
                disabled={!algorandTransactionsData?.walletAddress}
              />
            </div>
            <AlgorandProcessingTransactions
              data={algorandTransactionsData}
              isLoading={algorandTransactionsLoading}
            />
          </>
        )}
      </Card>
    </>
  );
};
